import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, ChevronLeft, ChevronRight, Send } from 'lucide-react'
import { api, type OutreachInput } from '@/api/client'
import { previewDebounce, useCreatorMutation, useCreatorPools, useOutreachPreview } from '@/lib/creators'
import { cn } from '@/lib/utils'
import { Button, ErrorState, PageHeader, Skeleton, errorText, useToast, useWidePage } from '@/ui'
import { Section } from '../campaigns/shared'
import { StepMessage } from './StepMessage'
import { StepSend } from './StepSend'
import { StepSplit } from './StepSplit'
import { StepTarget } from './StepTarget'
import { buildFilter, buildSplit, groupInputs, messageProblem, newState, sendProblem, type WizardState } from './wizard'

const STEPS = ['대상', '나누기', '메시지', '보내기 설정']

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return v
}

/** /creators/outreaches/new: 새 발송, in four steps. All the fields live here,
 *  so going back keeps what was typed. */
export default function NewOutreach() {
  useWidePage()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const pools = useCreatorPools()
  const [step, setStep] = useState(0)
  const [s, setS] = useState<WizardState>(() => newState(Number(params.get('pool')) || 0))
  const [error, setError] = useState<string | null>(null)
  const created = useRef<number | null>(null)
  const set = (patch: Partial<WizardState>) => setS((cur) => ({ ...cur, ...patch }))

  const poolItems = pools.data?.items ?? []
  const pool = poolItems.find((p) => p.id === s.poolId)
  // The language follows the pool the first time it is known.
  const langFor = useRef(0)
  useEffect(() => {
    if (pool && langFor.current !== pool.id) {
      langFor.current = pool.id
      setS((cur) => ({ ...cur, language: pool.language }))
    }
  }, [pool])

  const filter = buildFilter(s)
  const split = buildSplit(s)
  const body = useMemo(
    () => (s.poolId > 0 && !filter.error && !split.error ? { pool_id: s.poolId, filter: filter.value, split: split.value } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify([s.poolId, filter.value, split.value, filter.error, split.error])],
  )
  const debounced = useDebounced(body, previewDebounce.ms)
  const preview = useOutreachPreview(debounced)
  const groups = preview.data?.groups ?? []
  const eligible = preview.data?.eligible ?? 0

  const create = useCreatorMutation(async () => {
    let id = created.current
    if (id == null) {
      const payload: OutreachInput = {
        name: s.name.trim(),
        pool_id: s.poolId,
        filter: filter.value,
        split: split.value,
        product_name: s.productName.trim(),
        product_url: s.productUrl.trim(),
        product_brief: s.productBrief.trim(),
        language: s.language,
        signoff: s.signoff.trim(),
        daily_limit: Number(s.daily),
        send_start_hour: Number(s.startHour),
        send_end_hour: Number(s.endHour),
        timezone: s.timezone,
        ...(pool?.platform === 'bluesky' ? { bluesky_target_id: Number(s.sender) } : {}),
        groups: groupInputs(s, groups),
      }
      id = (await api.createOutreach(payload)).outreach.id
      created.current = id
    }
    return id
  })

  const blocked = (i: number): string | null => {
    if (i >= 0 && (!pool || filter.error)) return filter.error ?? '풀을 고른다.'
    if (i === 0 && preview.data && eligible === 0) return '연락할 수 있는 사람이 없다. 조건을 바꾼다.'
    if (i === 0 && !preview.data) return preview.error ? errorText(preview.error) : '사람 수를 세는 중이다.'
    if (i >= 1 && split.error) return split.error
    if (i === 1 && !preview.data) return '나누는 중이다.'
    if (i >= 2 && groups.length === 0) return '나눈 그룹이 없다. 앞 단계를 다시 본다.'
    if (i === 2) return messageProblem(s, groups, pool?.platform)
    if (i === 3) return sendProblem(s, pool?.platform === 'bluesky')
    return null
  }
  const problem = blocked(step)

  const go = (to: number) => {
    if (to > step) for (let i = step; i < to; i++) if (blocked(i)) return
    if (to === 3 && !s.name.trim() && pool) {
      const d = new Date()
      set({ name: `${pool.name} · ${d.getMonth() + 1}/${d.getDate()}` })
    }
    setError(null)
    setStep(to)
  }

  const submit = async () => {
    setError(null)
    const p = [0, 1, 2, 3].map(blocked).find(Boolean)
    if (p) {
      setError(p)
      return
    }
    let id: number
    try {
      id = await create.mutateAsync()
    } catch (e) {
      setError(errorText(e))
      return
    }
    try {
      await api.generateOutreach(id)
      toast.success('발송을 만들고 초안을 쓰기 시작했다')
    } catch (e) {
      toast.error(`발송은 만들었다. 초안은 시작하지 못했다: ${errorText(e)}`)
    }
    navigate(`/creators/outreaches/${id}`)
  }

  const parent = { to: '/creators/outreaches', label: 'DM 보내기' }
  if (pools.error && !pools.data) {
    return (
      <>
        <PageHeader title="새 발송" parent={parent} />
        <ErrorState title="풀을 불러오지 못했다" error={pools.error} onRetry={() => pools.refetch()} />
      </>
    )
  }
  if (pools.isPending) {
    return (
      <>
        <PageHeader title="새 발송" parent={parent} />
        <Skeleton className="h-64" />
      </>
    )
  }

  return (
    <>
      <PageHeader title="새 발송" parent={parent} description="풀에서 사람을 고르고, 나누고, 그룹마다 메시지를 정한다." />
      <ol aria-label="단계" className="mb-4 grid grid-cols-4 gap-1.5">
        {STEPS.map((label, i) => {
          const done = i < step
          const on = i === step
          return (
            <li key={label} className="min-w-0">
              <button
                type="button"
                aria-current={on ? 'step' : undefined}
                onClick={() => go(i)}
                className={cn(
                  'flex w-full min-w-0 flex-col items-start gap-0.5 border-t-2 pt-2 text-left sm:flex-row sm:items-center sm:gap-2',
                  on ? 'border-foreground text-foreground' : done ? 'border-status-published text-foreground' : 'border-border text-muted-foreground',
                )}
              >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-micro font-semibold tabular-nums">
                  {done ? <Check size={12} aria-hidden /> : i + 1}
                </span>
                <span className="w-full truncate text-meta font-medium sm:text-table">{label}</span>
              </button>
            </li>
          )
        })}
      </ol>

      <Section title={`${step + 1}. ${STEPS[step]}`} className="mb-4">
        {step === 0 && <StepTarget s={s} set={set} pools={poolItems} preview={preview} problem={filter.error} />}
        {step === 1 && <StepSplit s={s} set={set} preview={preview} problem={split.error} />}
        {step === 2 && <StepMessage s={s} set={set} groups={groups} platform={pool?.platform} />}
        {step === 3 && (
          <StepSend s={s} set={set} platform={pool?.platform} groupCount={groups.length} eligible={eligible} />
        )}
      </Section>

      {(error || (problem && step > 0 && step === 3)) && (
        <p role="alert" className="mb-3 text-table text-status-failed">
          {error ?? problem}
        </p>
      )}
      <div className="flex items-center justify-between gap-2 pb-2">
        <Button icon={ChevronLeft} disabled={step === 0} onClick={() => go(step - 1)}>
          이전
        </Button>
        <div className="flex min-w-0 items-center gap-3">
          {problem && step < 3 && !(step === 1 && split.error) && <span className="hidden min-w-0 truncate text-meta text-muted-foreground sm:inline">{problem}</span>}
          {step < 3 ? (
            <Button variant="primary" disabled={!!problem} onClick={() => go(step + 1)}>
              다음
              <ChevronRight size={16} aria-hidden />
            </Button>
          ) : (
            <Button variant="primary" icon={Send} loading={create.isPending} onClick={() => void submit()}>
              만들고 초안 쓰기
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
